import type { ReactNode } from 'react';
import { useState } from 'react';
import { Check, Lock } from 'lucide-react';
import { db } from '@/lib/db';
import type { Member, Visibility } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  member: Member;
  onClose: () => void;
  onSaved: (m: Member) => void;
}

function RadioOption({
  selected,
  onSelect,
  title,
  children,
  icon,
}: {
  selected: boolean;
  onSelect: () => void;
  title: ReactNode;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`mb-2 flex w-full items-start gap-3 rounded-xl border-[1.5px] p-3.5 text-left ${
        selected ? 'border-gold bg-gold/10' : 'border-line bg-paper'
      }`}
    >
      <div
        className={`mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-gold bg-gold' : 'border-line bg-transparent'
        }`}
      >
        {selected && <Check size={12} className="text-navy" strokeWidth={3} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-bold text-navy">
          {icon}
          {title}
        </div>
        <div className="mt-1 text-[11.5px] leading-[1.45] text-ink-soft">{children}</div>
      </div>
    </button>
  );
}

export function PrivacySheet({ member, onClose, onSaved }: Props) {
  const [visibility, setVisibility] = useState<Visibility>(member.visibility);
  const [saving, setSaving] = useState(false);
  const dirty = visibility !== member.visibility;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      onSaved(await db.members.update({ visibility }));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title="Privacy" onClose={onClose}>
      <div className="px-4 pb-6 pt-2">
        <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">WHO CAN SEE YOUR POSTS</div>

        <RadioOption
          selected={visibility === 'public'}
          onSelect={() => setVisibility('public')}
          title="Public to the club"
        >
          Every Bag Tag member at ANCC can see your posts, scores, and reactions. This is the default — it makes the social feed work for everyone.
        </RadioOption>

        <RadioOption
          selected={visibility === 'followers'}
          onSelect={() => setVisibility('followers')}
          title="Followers only"
          icon={<Lock size={14} />}
        >
          Only members who follow you see your posts, scores, and reactions. Your profile still appears in the club directory, and you&apos;re still listed on tournament leaderboards and the Hole-in-One Wall.
        </RadioOption>

        <div className="mb-4 rounded border-l-2 border-gold bg-gold/10 px-3 py-2.5 text-[10.5px] leading-[1.5] text-ink-soft">
          <strong className="text-navy">What stays visible to all members regardless of this setting:</strong>{' '}
          your profile (name, handle, handicap, avatar) in the directory; your name on tournament leaderboards you&apos;ve joined; your aces on the Hole-in-One Wall; your scores in games you&apos;ve played with others.
        </div>

        <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">COMING SOON</div>
        <div className="mb-4 rounded-[10px] border border-line bg-paper p-3">
          <div className="text-[11.5px] leading-[1.5] text-ink-soft">
            More controls in development: hide military service from directory (OPSEC option for active-duty members), block specific members, control photo tagging, export or delete your data.
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[10px] border border-line bg-cream py-3 text-[13px] font-bold text-ink-soft"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            className={`flex-[2] rounded-[10px] py-3 text-[13px] font-bold ${
              dirty ? 'bg-navy text-paper' : 'bg-line text-mute'
            } disabled:opacity-60`}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
